import { useCallback, useEffect, useMemo, useState } from "react";
import { customerApi } from "../../../apis/customer/api";
import { drayageApi } from "../../../apis/drayage/api";
import { otrApi } from "../../../apis/otr/api";
import { CalculateOTRQuoteParams } from "../../../apis/otr/types";
import { useNotification } from "../../../contextApis/NotificationContext";
import { useRequiredData } from "../../../contextApis/RequiredDataContext";
import { Option } from "../../../types/common";
import {
  BaseCustomerProfileParams,
  CustomerProfile,
  LocationParams,
  OperationExpense,
} from "../../../types/drayage";
import { LoadingTypesEnum } from "../../../types/loading";
import {
  BaseLoadTypesEnum,
  OTR_LOAD_TYPE_OPTIONS,
} from "../../../types/loadTypes";
import { OTRLocationAddress, RouteCostBrakeDown } from "../../../types/otr";
import { getApiErrorForDisplay } from "../../../utils/apiError";
import { initialOEParams } from "./Search.data";

interface UIState {
  loading: LoadingTypesEnum;
  isNewCustomerProfileLoading: boolean;
  accordionOpenItems: string[];
  isAddCustomerProfileFormOpen: boolean;
}

const initialCalculationParams: CalculateOTRQuoteParams = {
  customerProfileId: null,
  operationalExpensesProfileId: null,
  loadType: OTR_LOAD_TYPE_OPTIONS[0].value as string,
  emptyLocationCoordinates: null,
  originCoordinates: null,
  destinationCoordinates: null,
  emptyLocation: null,
  originLocation: {
    city: "",
    state: "",
    zipCode: "",
    address: "",
  },
  destinationLocation: {
    city: "",
    state: "",
    zipCode: "",
    address: "",
  },
  externalProvider: "Outlook",
};

export const useSearch = () => {
  const { addNotification } = useNotification();
  const { loadTypes, companyDetails } = useRequiredData();

  const [error, setError] = useState<string>();

  const [uiState, setUiState] = useState<UIState>({
    loading: LoadingTypesEnum.idle,
    isNewCustomerProfileLoading: false,
    isAddCustomerProfileFormOpen: false,
    accordionOpenItems: [],
  });

  const [profiles, setProfiles] = useState({
    customer: [] as CustomerProfile[],
    oe: [] as OperationExpense[],
  });

  const [calculationParams, setCalculationParams] =
    useState<CalculateOTRQuoteParams>(initialCalculationParams);

  const [isHazmat, setIsHazmat] = useState(false);

  const [routeCostBreakdown, setRouteCostBreakdown] =
    useState<RouteCostBrakeDown>();
  const [quoteRequestId, setQuoteRequestId] = useState<number>();

  const isHazmatCertified = useMemo(
    () => loadTypes?.loadTypeConfigurations?.isHazmatCertified || false,
    [loadTypes],
  );

  const loadTypeOptions = OTR_LOAD_TYPE_OPTIONS;

  const handleProfilesOptions = useCallback(
    (profiles: Array<{ id: number; name: string }>): Option[] => {
      return profiles.map((profile) => ({
        value: profile.id,
        label: profile.name,
      }));
    },
    [],
  );

  const profileOptions = useMemo(
    () => ({
      customer: handleProfilesOptions(profiles.customer),
      oe: handleProfilesOptions(profiles.oe),
    }),
    [profiles, handleProfilesOptions],
  );

  const onCustomerProfileChange = (newId: string) => {
    setCalculationParams((prev) => ({
      ...prev,
      customerProfileId: +newId,
    }));
  };

  const onOperationalExpenseChange = (newId: string) => {
    setCalculationParams((prev) => ({
      ...prev,
      operationalExpensesProfileId: +newId,
    }));
  };

  const onLoadTypeChange = (newLoadType: string) => {
    setCalculationParams((prev) => ({
      ...prev,
      loadType: newLoadType as BaseLoadTypesEnum,
    }));
  };

  const onHazmatToggle = (isChecked: boolean) => {
    setIsHazmat(isChecked);
  };

  const onLocationChange = useCallback(
    (location: LocationParams, type: "origin" | "destination" | "empty") => {
      const { coords, ...rest } = location;
      const locationData: OTRLocationAddress = {
        zipCode: rest.zipCode ?? null,
        city: rest.city ?? null,
        state: rest.state ?? null,
        address: rest.address ?? null,
      };

      switch (type) {
        case "origin":
          setCalculationParams((prev) => ({
            ...prev,
            originCoordinates: coords,
            originLocation: locationData,
          }));
          break;
        case "destination":
          setCalculationParams((prev) => ({
            ...prev,
            destinationCoordinates: coords,
            destinationLocation: locationData,
          }));
          break;
        case "empty":
          setCalculationParams((prev) => ({
            ...prev,
            emptyLocationCoordinates: coords,
            emptyLocation: locationData,
          }));
          break;
        default:
          break;
      }
    },
    [],
  );

  const onFetchOeProfiles = useCallback(async () => {
    try {
      const data =
        await drayageApi.getOperationalExpensesProfiles(initialOEParams);

      if (data.hasError) {
        addNotification({
          type: "error",
          message: getApiErrorForDisplay(data.errors),
        });
      }

      setProfiles((prev) => ({
        ...prev,
        oe: data.operationalExpensesProfiles,
      }));
    } catch (error) {
      addNotification({ type: "error", message: error });
    }
  }, []);

  const onFetchCustomerProfiles = useCallback(async (searchQuery: string) => {
    try {
      const data = await customerApi.getCustomerProfilesSuggestions({
        searchQuery,
      });

      if (data.hasError) {
        addNotification({
          type: "error",
          message: getApiErrorForDisplay(data.errors),
        });
      }

      setProfiles((prev) => ({
        ...prev,
        customer: data.customerProfileSuggestions,
      }));
    } catch (error) {
      addNotification({ type: "error", message: error });
    }
  }, []);

  const onGetRouteEstimates = useCallback(async () => {
    try {
      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.pending }));

      if (uiState.accordionOpenItems.length === 0) {
        setUiState((prev) => ({ ...prev, accordionOpenItems: ["map"] }));
      }

      const loadTypeFormatted: CalculateOTRQuoteParams = {
        ...calculationParams,
        loadType: `${calculationParams.loadType}${isHazmat ? "WithHazmat" : ""}`,
      };

      const data = await otrApi.calculateOTRQuote(loadTypeFormatted);

      setRouteCostBreakdown(data.routeCostBreakdown);
      setQuoteRequestId(data.calculateRouteRequestId);

      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.fulfilled }));
    } catch (error) {
      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.rejected }));
      addNotification({
        type: "error",
        message: error.toString(),
      });
    }
  }, [addNotification, calculationParams, isHazmat]);

  const onClearForm = useCallback(() => {
    setCalculationParams(initialCalculationParams);
    setRouteCostBreakdown(undefined);
    setQuoteRequestId(undefined);
    setError(undefined);
  }, []);

  const onCustomerProfileFormSubmit = useCallback(
    async (data: BaseCustomerProfileParams) => {
      try {
        setUiState((prev) => ({ ...prev, isNewCustomerProfileLoading: true }));

        const response = await customerApi.addCustomerProfile(data);
        await onFetchCustomerProfiles("");

        if (response) {
          setCalculationParams((prev) => ({
            ...prev,
            customerProfileId: response.id,
          }));
        }

        setUiState((prev) => ({ ...prev, isNewCustomerProfileLoading: false }));
      } catch (error) {
        setUiState((prev) => ({ ...prev, isNewCustomerProfileLoading: false }));

        addNotification({
          type: "error",
          message: error.toString(),
        });
      }
    },
    [addNotification],
  );

  const setOfferPrice = useCallback(
    async (id: number, offeredPrice: number, isOfferAccepted: boolean) => {
      try {
        await otrApi.setOfferPrice({
          calculateRouteRequestId: id,
          offeredPrice,
          isOfferAccepted,
        });
      } catch (error) {
        addNotification({ type: "error", message: error });
      }
    },
    [addNotification],
  );

  const areCoordsEqual =
    calculationParams.originCoordinates?.latitude ===
      calculationParams.destinationCoordinates?.latitude &&
    calculationParams.originCoordinates?.longitude ===
      calculationParams.destinationCoordinates?.longitude;

  const isSuccessButtonDisabled =
    !calculationParams.originLocation ||
    !calculationParams.destinationLocation ||
    uiState.loading === LoadingTypesEnum.pending ||
    areCoordsEqual;

  useEffect(() => {
    onFetchOeProfiles();
    onFetchCustomerProfiles("");
  }, []);

  return {
    error,
    loading: uiState.loading,
    isHazmatCertified,
    isNewCustomerProfileLoading: uiState.isNewCustomerProfileLoading,
    isAddCustomerProfileFormOpen: uiState.isAddCustomerProfileFormOpen,
    accordionOpenItems: uiState.accordionOpenItems,
    companyDetails,
    companyProfileOptions: profileOptions.customer,
    oeProfileOptions: profileOptions.oe,
    loadTypeOptions,
    selectedCustomerProfile: calculationParams.customerProfileId,
    selectedOeProfile: calculationParams.operationalExpensesProfileId,
    selectedLoadType: calculationParams.loadType,
    isHazmat,
    quoteRequestId,
    routeCostBreakdown,
    onCustomerProfileChange,
    onOperationalExpenseChange,
    onLoadTypeChange,
    onHazmatToggle,
    onLocationChange,
    onGetRouteEstimates,
    onClearForm,
    onCustomerProfileFormSubmit,
    setOfferPrice,
    setUiState: (updates: Partial<UIState>) =>
      setUiState((prev) => ({ ...prev, ...updates })),
    isSuccessButtonDisabled,
  };
};

export default useSearch;
